import { MenuItem } from "@blueprintjs/core"
import CustomButton from "components/Atoms/CustomButton"
import CustomFormGroup from "components/Atoms/CustomFormGroup"
import StaticSelector from "components/Atoms/StaticSelector"
import ButtonMutation from "components/Molecules/ButtonMutation"
import DynamicSelector from "components/Molecules/DynamicSelector"
import dayjs from "dayjs"
import { useDmsBulkAssignMutation } from "generated/graphql"
import { DISPATCH_VEHICLES } from "graphql/dispatch/dispatch.queries.gql"
import { useAllDocksList } from "graphql/dms/dms.hooks.gql"
import { UPDATE_TRIP } from "graphql/trip/trip.mutations.gql"
import { useSearchFilter } from "hooks/searchFilterHooks"
import { cloneDeep, get, set } from "lodash-es"
import React, { useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import { ApolloErrorToast, SuccessToast } from "utils/AppToaster"
import ArrivedVehicleDialog from "./ArrivedVehicleDialog"
import ItemsTable from "./ItemsTable"
import OrdersTable from "./OrdersTable"
import SellableItemsTable from "./SellableItemsTable"
import SupervisorSuggest from "./SupervisorSuggest"

const Loading = ({ tripId, trip }) => {
  const navigate = useNavigate()
  const allDocks = useAllDocksList()
  const [bulkAssignment] = useDmsBulkAssignMutation()
  const [arrivedDockDialog, setArrivedDockDialog] = useState(false)

  const [{ order: selectedOrder, loadingPattern }] = useSearchFilter()

  const [state, setState] = useState<any>({
    arrivedAtDock: false,
    timestamp: new Date(),
    estimatedDespatchTime: "",
    scheduledVehicle: {},
    arrivedVehicle: {},
    supervisor: {},
    dock: {},
  })

  function handleChange(path, value) {
    const stateCopy = cloneDeep(state)
    if (path === "orderingDcId") {
      set(stateCopy, "userId", null)
      if (value.id === state.targetDcId?.id) {
        set(stateCopy, "targetDcId", null)
      }
    }
    set(stateCopy, path, value)
    setState(stateCopy)
  }

  useEffect(() => {
    if (trip) {
      const stateCopy = cloneDeep(state)
      set(stateCopy, "estimatedDespatchTime", trip?.estimatedDespatchTime)
      set(stateCopy, "scheduledVehicle", trip?.scheduledVehicle)
      set(stateCopy, "arrivedVehicle", trip?.vehicle)
      set(stateCopy, "dock", trip?.dock)
      set(stateCopy, "supervisor", trip?.dockSupervisor)
      set(
        stateCopy,
        "arrivedAtDock",
        trip?.tripLogs?.some(
          log => log?.step?.stepId === "10ac3986-cbad-425c-b0ed-0fd858b64a1a"
        )
      )
      setState(stateCopy)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trip])

  return (
    <div className="p-2">
      <ArrivedVehicleDialog
        tripId={tripId}
        isOpen={arrivedDockDialog}
        onClose={() => setArrivedDockDialog(false)}
      />

      <div className="grid grid-cols-12 gap-4 mt-3">
        <div className="col-span-6">
          Load scheduled:{" "}
          <span style={{ fontWeight: "bold" }}>
            {dayjs(new Date(state.estimatedDespatchTime)).format("h:mm a")}
          </span>
        </div>
      </div>

      <div className="grid grid-cols-12 gap-4 mt-3">
        <div className="col-span-3">
          <DynamicSelector
            label="Sceduled vehicle"
            className="ml-1 mb-0"
            disabled={true}
            searchVector={["registrationNumber"]}
            name={["registrationNumber"]}
            value={state.scheduledVehicle}
            query={DISPATCH_VEHICLES}
            onChange={(_, value) => {
              handleChange("scheduledVehicle", value)
            }}
            itemRenderer={(item, { handleClick, modifiers }) => {
              return (
                <MenuItem
                  labelElement={
                    <span>
                      {`${get(item, "vehicleType.name")}, `}
                      <span className="vt-highlight">
                        {item?.vehicleType?.capacity}{" "}
                      </span>
                      {`${get(item, "vehicleType.capacityUnit.codename")}`}
                    </span>
                  }
                  icon="drive-time"
                  active={modifiers.active}
                  key={item?.id}
                  onClick={handleClick}
                  text={item.registrationNumber}
                />
              )
            }}
          />
        </div>
        <div className="col-span-3">
          <DynamicSelector
            label="Arrived vehicle"
            className="ml-1 mb-0"
            searchVector={["registrationNumber"]}
            name={["registrationNumber"]}
            value={state.arrivedVehicle}
            query={DISPATCH_VEHICLES}
            onChange={(_, value) => {
              handleChange("arrivedVehicle", value)
            }}
            itemRenderer={(item, { handleClick, modifiers }) => {
              return (
                <MenuItem
                  labelElement={
                    <span>
                      {`${get(item, "vehicleType.name")}, `}
                      <span className="vt-highlight">
                        {item?.vehicleType?.capacity}{" "}
                      </span>
                      {`${get(item, "vehicleType.capacityUnit.codename")}`}
                    </span>
                  }
                  icon="drive-time"
                  active={modifiers.active}
                  key={item?.id}
                  onClick={handleClick}
                  text={item.registrationNumber}
                />
              )
            }}
          />
        </div>
      </div>

      <div className="grid grid-cols-12 gap-4 mt-3">
        <div className="col-span-3">
          <StaticSelector
            path="dock"
            label="Call vehicle at dock"
            absolute
            items={allDocks || []}
            value={state.dock}
            onChange={handleChange}
          />
        </div>
        <div className="col-span-3">
          <SupervisorSuggest
            path="supervisor"
            value={state.supervisor?.firstName ? state.supervisor : null}
            onChange={handleChange}
          />
        </div>
      </div>

      <div className="grid grid-cols-12 gap-4">
        <div className="col-span-3"></div>
        <div className="col-span-3">
          <ButtonMutation
            style={{ float: "right" }}
            className="mt-6"
            icon="confirm"
            text="Save"
            //disabled={state.arrivedAtDock}
            mutation={UPDATE_TRIP}
            variables={{
              tripId,
              vehicleId: state.arrivedVehicle?.id,
            }}
            onError={err => {
              ApolloErrorToast(err)
            }}
            onCompleted={() => {
              bulkAssignment({
                variables: {
                  tripIds: [tripId],
                  dockId: state.dock?.id,
                  dockSupervisorId: state?.supervisor?.id,
                },
              })
              SuccessToast(`Success`)
            }}
          />
        </div>
      </div>

      <div className="grid grid-cols-12 gap-4 mt-3">
        <div className="col-span-3">
          <CustomFormGroup
            inline={true}
            label={`Vehicle at dock: ${state?.dock?.name || ""}`}
          >
            <CustomButton
              intent="primary"
              text="Arrived"
              disabled={state.arrivedAtDock}
              onClick={() => setArrivedDockDialog(true)}
            />
          </CustomFormGroup>
        </div>
      </div>

      {state.arrivedAtDock && (!loadingPattern || loadingPattern === "ORD") && (
        <div className="border-legacy">
          <div className="grid grid-cols-2 gap-4 mt-6">
            <div>
              <OrdersTable tripId={tripId} orders={trip?.orders} />
            </div>
            <div>
              <ItemsTable
                tripId={tripId}
                items={
                  trip?.orders?.filter(o => o.trackingCode === selectedOrder)[0]
                    ?.bookingsByTrip
                }
              />
            </div>
          </div>
          <div className="grid grid-cols-1 gap-4">
            <div className="m-4">
              <CustomButton
                style={{ float: "right" }}
                intent="success"
                text="Check SKUs"
                disabled={
                  trip?.orders?.filter(o => o.orderStatus !== "DES").length > 0
                }
                onClick={() =>
                  navigate(`/d/ecom/dms/trip/${tripId}/check-skus`)
                }
              />
            </div>
          </div>
        </div>
      )}

      {state.arrivedAtDock && loadingPattern && loadingPattern === "SLI" && (
        <div>
          <div className="grid grid-cols-1 gap-4 mt-6">
            <div>
              <SellableItemsTable tripId={tripId} />
            </div>
          </div>
          <div className="grid grid-cols-1 gap-4">
            <div className="m-4">
              <CustomButton
                style={{ float: "right" }}
                intent="success"
                text="Check SKUs"
                // disabled={
                //   trip?.orders?.filter(o => o.orderStatus !== "DES").length > 0
                // }
                onClick={() =>
                  navigate(`/d/ecom/dms/trip/${tripId}/check-skus`)
                }
              />
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default Loading
